import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Report } from './reportsStore';

// Define the structure for stored comparison items
export interface CompareItem {
  id: string; // Unique instance ID (e.g., slug-index)
  report: Report;
}

export const useCompareStore = defineStore('compare', () => {
  // Store an array of CompareItem objects
  const selectedReports = ref<CompareItem[]>([]);

  // Computed property to get all unique groups from selected reports
  const selectedGroups = computed(() => {
    if (selectedReports.value.length === 0) return [];
    
    if (selectedReports.value.length === 1) {
      return selectedReports.value[0].report.groups || [];
    }
    
    return selectedReports.value.reduce((commonGroups, item) => {
      const reportGroups = item.report.groups || [];
      return commonGroups.filter(group => reportGroups.includes(group));
    }, selectedReports.value[0].report.groups || []);
  });

  // Check if a report is compatible with currently selected reports
  const isReportCompatible = computed(() => (report: Report) => {
    if (selectedReports.value.length === 0) return true;
    
    const reportGroups = report.groups || [];
    
    // If report has no groups, it's not compatible
    if (reportGroups.length === 0) return false;
    
    // Check if the report shares at least one group with the selected groups
    return selectedGroups.value.some(group => reportGroups.includes(group));
  });

  // Check if we can add more reports (limit of 3)
  const canAddMoreReports = computed(() => selectedReports.value.length < 3);

  // Check if a specific report is already selected
  const isReportSelected = computed(() => (instanceId: string) => {
    return selectedReports.value.some(item => item.id === instanceId);
  });

  // Add report by instance ID and report data
  function addReport(instanceId: string, reportData: Report) {
    // Check if already selected
    if (isReportSelected.value(instanceId)) return false;
    
    // Check limit
    if (!canAddMoreReports.value) return false;
    
    // Check compatibility
    if (!isReportCompatible.value(reportData)) return false;

    selectedReports.value.push({ id: instanceId, report: reportData });
    return true;
  }

  // Remove report by instance ID
  function removeReport(instanceId: string) {
    selectedReports.value = selectedReports.value.filter(item => item.id !== instanceId);
  }

  function clearReports() {
    selectedReports.value = [];
  }

  return {
    selectedReports,
    selectedGroups,
    isReportCompatible,
    canAddMoreReports,
    isReportSelected,
    addReport,
    removeReport,
    clearReports
  };
}); 